/**
 * Renderer: rasterizes the cell tree into the visible canvas.
 *
 * Hybrid strategy:
 * - every (cell, layer) with content is rasterized once into a small
 *   offscreen cache at a fixed resolution, then composited each frame;
 * - when a cell would be magnified beyond its cache resolution (deep zoom
 *   into a shallow cell), its elements are drawn as vectors at screen
 *   resolution instead, so lines stay crisp at any zoom and sub-pixel-in-tile
 *   strokes remain visible.
 *
 * Cache memory is bounded with an LRU; evicted tiles simply re-render.
 */

import { Camera } from "./camera";
import { CELL_EXTENT, WORLD_EXTENT, Cell } from "./cell";
import { Mat2D, Rect, Vec2, apply, inv, mul, rectsIntersect, transformRect } from "./math";
import { World } from "./world";
import { Element, Stroke } from "./model";
import { ribbonOutline, strokeBounds, tracePolygon, strokeWidthAt } from "./strokes";
import { pressureOf } from "./strokes";

export const CACHE_RES = 512; // px across [-CELL_EXTENT, CELL_EXTENT]
const CACHE_UNITS = CELL_EXTENT * 2;
/** Native tile texel density: tile px per cell unit. */
export const CACHE_PX_PER_UNIT = CACHE_RES / CACHE_UNITS;
const CACHE_MAX_BYTES = 96 * 1024 * 1024; // LRU budget (~96 caches at 512px)
const MIN_CELL_SCREEN_PX = 4; // stop descending below this

interface Tile {
  canvas: HTMLCanvasElement;
  lastUsed: number;
}

const BLEND_TO_COMPOSITE: Record<string, GlobalCompositeOperation> = {
  normal: "source-over",
  multiply: "multiply",
  screen: "screen",
  erase: "destination-out",
};

/** Fill for the void outside the top-level paper (see WORLD_EXTENT). */
const OUTSIDE_FILL = "#e8e6df";
/** Stroke for the root-canvas edge. */
const BOUNDARY_STROKE = "#b3b0a4";
/** Canvas-edge line width in css px (constant on screen at any zoom). */
const BOUNDARY_LINE_WIDTH = 1.5;
/** Above this screen magnitude we treat the boundary as not visible (deep zoom). */
const HUGE_COORD = 1e7;

export class Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private world: World;
  private tiles = new Map<string, Tile>();
  private dirty = new Set<string>();
  private frame = 0;
  private cssW = 0;
  private cssH = 0;
  private dpr = 1;
  private bgColor = "#fdfdfb";

  constructor(canvas: HTMLCanvasElement, world: World, bgColor?: string) {
    this.canvas = canvas;
    this.world = world;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
    if (bgColor) this.bgColor = bgColor;
    world.on("changed", ({ cellId, layerId }) => {
      this.dirty.add(tileKey(cellId, layerId));
    });
    world.on("layersChanged", () => {
      for (const key of this.tiles.keys()) this.dirty.add(key);
    });
  }

  setBackground(color: string): void {
    this.bgColor = color;
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    this.cssW = cssW;
    this.cssH = cssH;
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.round(cssW * dpr));
    this.canvas.height = Math.max(1, Math.round(cssH * dpr));
  }

  get size(): { w: number; h: number } {
    return { w: this.cssW, h: this.cssH };
  }

  /**
   * Render the world through `camera`. Also usable to render to an external
   * context (exports) by passing targetCtx + width/height.
   */
  render(
    camera: Camera,
    opts?: { ctx?: CanvasRenderingContext2D; width?: number; height?: number; dpr?: number },
  ): void {
    const ctx = opts?.ctx ?? this.ctx;
    const w = opts?.width ?? this.cssW;
    const h = opts?.height ?? this.cssH;
    const dpr = opts?.dpr ?? this.dpr;
    this.frame++;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    // Two-tone backdrop: the top-level paper is paper, the void
    // outside it is shaded — so zooming/panning past the canvas edge reads
    // as a visible boundary. Content draws on top of both fills below.
    const boundary = canvasBoundary(camera, w, h);
    if (!boundary || boundary.placement === "contains") {
      ctx.fillStyle = this.bgColor;
      ctx.fillRect(0, 0, w, h);
    } else if (boundary.placement === "outside") {
      ctx.fillStyle = OUTSIDE_FILL;
      ctx.fillRect(0, 0, w, h);
    } else {
      ctx.fillStyle = OUTSIDE_FILL;
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = this.bgColor;
      tracePoly(ctx, boundary.corners);
      ctx.fill();
    }

    // Viewport rect in focus-cell space.
    const view = this.viewportRect(camera, w, h);
    const cx = w / 2;
    const cy = h / 2;

    type DrawItem =
      | { kind: "tile"; tile: Tile; m: Mat2D; opacity: number; blend: GlobalCompositeOperation }
      | {
          kind: "vector";
          els: Element[];
          m: Mat2D;
          opacity: number;
          cellId: string;
          layerId: string;
        };

    const drawList: DrawItem[] = [];

    this.world.tree.visit(
      camera.focusCellId,
      view,
      (cell) => this.shouldDescend(camera, cell),
      (cell) => {        const cellToScreen = camera.cellToScreen(cx, cy, cell.id);
        // Screen (css) px per cell unit. The tile cache holds
        // CACHE_RES / CACHE_UNITS px per unit; when the screen needs more
        // (deep zoom into a shallow cell), compositing the tile would upscale
        // and blur — and strokes thinner than a tile pixel would vanish. In
        // that case draw the cell's elements as vectors at screen resolution
        // so lines stay crisp at any zoom.
        const screenScale = linearScaleOf(cellToScreen);
        const upscale = (screenScale * dpr) / CACHE_PX_PER_UNIT;
        const useVector = upscale > 1.1;
        for (const layer of this.world.layers) {
          if (!layer.visible || layer.opacity <= 0) continue;
          const els = this.world.elementsInCell(cell.id, layer.id);
          if (els.length === 0) continue;
          if (useVector && layer.blend === "normal" && !hasEraser(els)) {
            drawList.push({
              kind: "vector",
              els,
              m: cellToScreen,
              opacity: layer.opacity,
              cellId: cell.id,
              layerId: layer.id,
            });
            // Keep the tile cache warm so zooming back out is instant.
            const key = tileKey(cell.id, layer.id);
            this.ensureTile(key, cell, layer.id);
          } else {
            const key = tileKey(cell.id, layer.id);
            const tile = this.ensureTile(key, cell, layer.id);
            drawList.push({
              kind: "tile",
              tile,
              m: cellToScreen,
              opacity: layer.opacity,
              blend: BLEND_TO_COMPOSITE[layer.blend] ?? "source-over",
            });
          }
        }
      },
    );

    for (const d of drawList) {
      if (d.kind === "tile") {
        ctx.globalCompositeOperation = d.blend;
        ctx.globalAlpha = d.opacity;
        // drawImage's source coords are TILE PIXELS: map tile px -> cell units
        // (inverse of the cache scale), then cell units -> screen.
        const m = mul(d.m, tilePxToCellUnits());
        ctx.setTransform(dpr * m.a, dpr * m.b, dpr * m.c, dpr * m.d, dpr * m.e, dpr * m.f);
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(d.tile.canvas, 0, 0);
      } else {
        // Crisp vector path: cell units -> screen device px directly.
        const m = mul(dprScale(dpr), d.m);
        ctx.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
        ctx.globalCompositeOperation = "source-over";
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        // Cull off-viewport strokes/shapes: magnified ancestors can hold
        // hundreds of strokes, but only a few intersect the viewport. Text
        // and images are always drawn (cheap and few, bounds unreliable).
        const viewInCell = transformRect(inv(d.m), { x: 0, y: 0, w, h });
        for (const el of d.els) {
          if (!vectorElementInView(el, viewInCell)) continue;
          this.renderElementVector(ctx, el, d.opacity);
        }
      }
    }

    // Live stroke preview (drawn vector-straight to screen; committed to the
    // tile cache on finalize).
    const active = this.world.activeStroke;
    if (active && active.style.tool !== "eraser" && active.points.length > 0) {
      // dpr must be applied LAST (device space), i.e. dprScale ∘ cellToScreen.
      // Applying it first would scale cell coordinates and offset the preview
      // by (dpr-1)× the distance from the canvas center.
      const m = mul(dprScale(dpr), camera.cellToScreen(cx, cy, active.cellId));
      ctx.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = active.style.opacity;
      ctx.fillStyle = active.style.color;
      const ribbon = ribbonOutline(active.points, (p) => strokeWidthAt(active, pressureOf(p)));
      ctx.beginPath();
      tracePolygon(ctx, ribbon.path);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    this.evictIfNeeded();
  }

  /**
   * Stroke the top-level paper edge so the boundary reads when
   * zoomed/panned past it. Call after tiles and grid so the edge stays crisp
   * on top. No-op when the edge is off-screen (normal zoomed-in drawing).
   */
  drawCanvasBorder(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    w: number,
    h: number,
    dpr: number,
  ): void {
    const boundary = canvasBoundary(camera, w, h);
    if (!boundary || boundary.placement !== "visible") return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.strokeStyle = BOUNDARY_STROKE;
    ctx.lineWidth = BOUNDARY_LINE_WIDTH;
    ctx.shadowColor = "rgba(60, 55, 40, 0.28)";
    ctx.shadowBlur = 10;
    tracePoly(ctx, boundary.corners);
    ctx.stroke();
    ctx.restore();
  }

  private viewportRect(camera: Camera, w: number, h: number): Rect {
    const m = inv(camera.viewTransform(w / 2, h / 2));
    return transformRect(m, { x: 0, y: 0, w, h });
  }

  private shouldDescend(camera: Camera, cell: Cell): boolean {
    // Descend while the cell is big enough on screen that its content could
    // still be visible, and while it may have content below.
    if (!cell.hasContent && cell.id !== camera.focusCellId) {
      // If it has no content and is not on the camera path, prune below.
      return this.onCameraPath(camera.focusCellId, cell.id);
    }
    const sizePx = 2 * camera.scale * Math.pow(2, camera.depth - cell.depth);
    return sizePx >= MIN_CELL_SCREEN_PX;
  }

  private onCameraPath(focusId: string, cellId: string): boolean {
    return focusId.startsWith(cellId) || cellId.startsWith(focusId);
  }

  private cellUnitsToPx(): Mat2D {
    return cellUnitsToPxM();
  }

  private ensureTile(key: string, cell: Cell, layerId: string): Tile {
    let tile = this.tiles.get(key);
    if (!tile) {
      tile = { canvas: document.createElement("canvas"), lastUsed: this.frame };
      tile.canvas.width = CACHE_RES;
      tile.canvas.height = CACHE_RES;
      this.tiles.set(key, tile);
    }
    tile.lastUsed = this.frame;
    if (this.dirty.has(key)) {
      this.dirty.delete(key);
      this.renderTile(tile.canvas, this.world.elementsInCell(cell.id, layerId));
    }
    return tile;
  }

  /** Replay a cell-layer's elements into its raster cache. */
  private renderTile(canvas: HTMLCanvasElement, els: Element[]): void {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(...(cellUnitsToPxArr() as [number, number, number, number, number, number]));
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    // Fills first so stroke/shape outlines stay visible on top.
    for (const el of els) {
      if (el.kind === "fill") this.renderFill(ctx, el);
    }
    for (const el of els) {
      switch (el.kind) {
        case "stroke":
          this.renderStroke(ctx, el);
          break;
        case "shape":
          this.renderShape(ctx, el);
          break;
        case "fill":
          break; // already drawn above
        case "text":
          this.renderText(ctx, el);
          break;
        case "image":
          this.renderImage(ctx, el);
          break;
      }
    }
  }

  private renderStroke(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
    const isErase = stroke.style.tool === "eraser";
    const widthAt = (p: number) => strokeWidthAt(stroke, pressureOf(p));
    const ribbon = ribbonOutline(stroke.points, widthAt);
    if (ribbon.path.length < 3) return;
    ctx.globalCompositeOperation = isErase ? "destination-out" : "source-over";
    ctx.globalAlpha = isErase ? 1 : stroke.style.opacity;
    ctx.fillStyle = stroke.style.color;
    ctx.beginPath();
    tracePolygon(ctx, ribbon.path);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
  }

  private renderShape(
    ctx: CanvasRenderingContext2D,
    el: Extract<Element, { kind: "shape" }>,
  ): void {
    ctx.globalAlpha = el.opacity;
    ctx.strokeStyle = el.color;
    ctx.fillStyle = el.color;
    ctx.lineWidth = Math.max(el.size, 1e-4);
    ctx.beginPath();
    if (el.shape === "rect") {
      ctx.rect(el.x, el.y, el.w, el.h);
    } else {
      ctx.ellipse(
        el.x + el.w / 2,
        el.y + el.h / 2,
        Math.abs(el.w) / 2,
        Math.abs(el.h) / 2,
        0,
        0,
        Math.PI * 2,
      );
    }
    if (el.filled) ctx.fill();
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  private renderFill(
    ctx: CanvasRenderingContext2D,
    el: Extract<Element, { kind: "fill" }>,
  ): void {
    if (el.points.length < 3) return;
    ctx.globalAlpha = el.opacity;
    ctx.fillStyle = el.color;
    ctx.beginPath();
    ctx.moveTo(el.points[0]!.x, el.points[0]!.y);
    for (let i = 1; i < el.points.length; i++) ctx.lineTo(el.points[i]!.x, el.points[i]!.y);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  private renderText(ctx: CanvasRenderingContext2D, el: Extract<Element, { kind: "text" }>): void {
    const baseSize = 48;
    const scale = el.fontSize / baseSize;
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.translate(el.x, el.y);
    ctx.scale(scale, scale);
    ctx.fillStyle = el.color;
    ctx.font = `${baseSize}px ${el.fontFamily}`;
    ctx.textBaseline = "top";
    const lines = el.text.split("\n");
    const lineHeight = baseSize * 1.2;
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i]!, 0, i * lineHeight);
    }
    ctx.restore();
  }

  private renderImage(ctx: CanvasRenderingContext2D, el: Extract<Element, { kind: "image" }>): void {
    const img = this.imageCache.get(el.src);
    if (!img || !img.complete) return;
    ctx.globalAlpha = el.opacity;
    ctx.drawImage(img, el.x, el.y, el.w, el.h);
    ctx.globalAlpha = 1;
  }

  /**
   * Draw a single element directly in cell-unit space with the context already
   * transformed to screen device px (crisp vector path for magnified cells).
   * `layerOpacity` is folded into the element alpha to match tile compositing
   * (tile bakes element alpha, then composites with layer opacity).
   */
  private renderElementVector(
    ctx: CanvasRenderingContext2D,
    el: Element,
    layerOpacity: number,
  ): void {
    switch (el.kind) {
      case "stroke": {
        const ribbon = ribbonOutline(el.points, (p) => strokeWidthAt(el, pressureOf(p)));
        if (ribbon.path.length < 3) return;
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = el.style.opacity * layerOpacity;
        ctx.fillStyle = el.style.color;
        ctx.beginPath();
        tracePolygon(ctx, ribbon.path);
        ctx.fill();
        ctx.globalAlpha = 1;
        break;
      }
      case "shape": {
        ctx.globalAlpha = el.opacity * layerOpacity;
        ctx.strokeStyle = el.color;
        ctx.fillStyle = el.color;
        ctx.lineWidth = Math.max(el.size, 1e-9);
        ctx.beginPath();
        if (el.shape === "rect") {
          ctx.rect(el.x, el.y, el.w, el.h);
        } else {
          ctx.ellipse(
            el.x + el.w / 2,
            el.y + el.h / 2,
            Math.abs(el.w) / 2,
            Math.abs(el.h) / 2,
            0,
            0,
            Math.PI * 2,
          );
        }
        if (el.filled) ctx.fill();
        ctx.stroke();
        ctx.globalAlpha = 1;
        break;
      }
      case "fill": {
        if (el.points.length < 3) break;
        ctx.globalAlpha = el.opacity * layerOpacity;
        ctx.fillStyle = el.color;
        ctx.beginPath();
        ctx.moveTo(el.points[0]!.x, el.points[0]!.y);
        for (let i = 1; i < el.points.length; i++) ctx.lineTo(el.points[i]!.x, el.points[i]!.y);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        break;
      }
      case "text": {
        const baseSize = 48;
        const scale = el.fontSize / baseSize;
        ctx.save();
        ctx.globalAlpha = layerOpacity;
        ctx.translate(el.x, el.y);
        ctx.scale(scale, scale);
        ctx.fillStyle = el.color;
        ctx.font = `${baseSize}px ${el.fontFamily}`;
        ctx.textBaseline = "top";
        const lines = el.text.split("\n");
        const lineHeight = baseSize * 1.2;
        for (let i = 0; i < lines.length; i++) {
          ctx.fillText(lines[i]!, 0, i * lineHeight);
        }
        ctx.restore();
        break;
      }
      case "image": {
        const img = this.imageCache.get(el.src);
        if (!img || !img.complete) return;
        ctx.globalAlpha = el.opacity * layerOpacity;
        ctx.drawImage(img, el.x, el.y, el.w, el.h);
        ctx.globalAlpha = 1;
        break;
      }
    }
  }

  private imageCache = new Map<string, HTMLImageElement>();
  private pendingImages = new Set<string>();

  /** Preload an image element (called when adding images). */
  preloadImage(src: string): void {
    if (this.imageCache.has(src) || this.pendingImages.has(src)) return;
    this.pendingImages.add(src);
    const img = new Image();
    img.onload = () => {
      this.imageCache.set(src, img);
      this.pendingImages.delete(src);
      // Redraw all tiles referencing this image.
      for (const [key, tile] of this.tiles) this.dirty.add(key);
    };
    img.onerror = () => this.pendingImages.delete(src);
    img.src = src;
  }

  private evictIfNeeded(): void {
    let bytes = this.tiles.size * CACHE_RES * CACHE_RES * 4;
    if (bytes <= CACHE_MAX_BYTES) return;
    const entries = [...this.tiles.entries()].sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    for (const [key, tile] of entries) {
      this.tiles.delete(key);
      bytes -= CACHE_RES * CACHE_RES * 4;
      if (bytes <= CACHE_MAX_BYTES) break;
    }
  }
}

function tileKey(cellId: string, layerId: string): string {
  return `${cellId}|${layerId}`;
}

/** Root-canvas corners in screen css px, or null when not computable. */
export function canvasBoundaryCorners(camera: Camera, w: number, h: number): Vec2[] | null {
  if (!(w > 0 && h > 0)) return null;
  let m: Mat2D;
  try {
    m = camera.cellToScreen(w / 2, h / 2, "");
  } catch {
    return null;
  }
  const e = WORLD_EXTENT;
  const corners = [
    { x: -e, y: -e },
    { x: e, y: -e },
    { x: e, y: e },
    { x: -e, y: e },
  ].map((p) => apply(m, p));
  if (!corners.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) return null;
  // Deep inside the tree the root corners are astronomically far away: the
  // viewport is trivially contained, so there is no edge to draw.
  if (corners.some((p) => Math.abs(p.x) > HUGE_COORD || Math.abs(p.y) > HUGE_COORD)) return null;
  return corners;
}

export type BoundaryPlacement = "contains" | "visible" | "outside";

/**
 * Where the viewport sits relative to the canvas polygon: "contains" when
 * drawing normally inside the canvas, "visible" when at least one canvas
 * edge crosses the viewport (zoomed/panned past it), "outside" when the
 * canvas is fully off-screen.
 */
export function boundaryPlacement(corners: Vec2[], w: number, h: number): BoundaryPlacement {
  const viewport = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  if (viewport.every((p) => pointInPoly(p, corners))) return "contains";
  const cornerInView = corners.some((p) => p.x >= 0 && p.x <= w && p.y >= 0 && p.y <= h);
  const viewInPoly = viewport.some((p) => pointInPoly(p, corners));
  if (cornerInView || viewInPoly || polysEdgesCross(corners, viewport)) return "visible";
  return "outside";
}

function canvasBoundary(camera: Camera, w: number, h: number): {
  corners: Vec2[];
  placement: BoundaryPlacement;
} | null {
  const corners = canvasBoundaryCorners(camera, w, h);
  if (!corners) return null;
  return { corners, placement: boundaryPlacement(corners, w, h) };
}

function tracePoly(ctx: CanvasRenderingContext2D, corners: Vec2[]): void {
  ctx.beginPath();
  ctx.moveTo(corners[0]!.x, corners[0]!.y);
  for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
  ctx.closePath();
}

function pointInPoly(p: Vec2, poly: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

function polysEdgesCross(a: Vec2[], b: Vec2[]): boolean {
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (segmentsCross(a[i]!, a[(i + 1) % a.length]!, b[j]!, b[(j + 1) % b.length]!)) return true;
    }
  }
  return false;
}

function segmentsCross(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean {
  const d1 = direction(p3, p4, p1);
  const d2 = direction(p3, p4, p2);
  const d3 = direction(p1, p2, p3);
  const d4 = direction(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function direction(a: Vec2, b: Vec2, c: Vec2): number {
  return (c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x);
}

function hasEraser(els: Element[]): boolean {
  for (const el of els) {
    if (el.kind === "stroke" && el.style.tool === "eraser") return true;
  }
  return false;
}

function linearScaleOf(m: Mat2D): number {
  return Math.sqrt(Math.abs(m.a * m.d - m.b * m.c));
}

function vectorElementInView(el: Element, view: Rect): boolean {
  switch (el.kind) {
    case "stroke": {
      const b = strokeBounds(el);
      return rectsIntersect(view, { x: b.minX, y: b.minY, w: b.maxX - b.minX, h: b.maxY - b.minY });
    }
    case "shape": {
      const pad = Math.abs(el.size) / 2;
      return rectsIntersect(view, {
        x: Math.min(el.x, el.x + el.w) - pad,
        y: Math.min(el.y, el.y + el.h) - pad,
        w: Math.abs(el.w) + pad * 2,
        h: Math.abs(el.h) + pad * 2,
      });
    }
    case "fill": {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const q of el.points) {
        minX = Math.min(minX, q.x);
        minY = Math.min(minY, q.y);
        maxX = Math.max(maxX, q.x);
        maxY = Math.max(maxY, q.y);
      }
      if (!Number.isFinite(minX)) return false;
      return rectsIntersect(view, { x: minX, y: minY, w: maxX - minX, h: maxY - minY });
    }
    case "text":
    case "image":
      return true;
  }
}

function dprScale(dpr: number): Mat2D {
  return { a: dpr, b: 0, c: 0, d: dpr, e: 0, f: 0 };
}

/** Cell unit -> tile pixel transform (used when painting caches). */
export function cellUnitsToPxM(): Mat2D {
  const s = CACHE_RES / CACHE_UNITS;
  const t = s * CELL_EXTENT;
  return { a: s, b: 0, c: 0, d: s, e: t, f: t };
}

/**
 * Tile pixel -> cell unit transform. When compositing a cache onto the
 * screen, drawImage's source coordinates are tile pixels, so the composite
 * must be: cellToScreen ∘ tilePxToCellUnits.
 */
export function tilePxToCellUnits(): Mat2D {
  const s = CACHE_RES / CACHE_UNITS;
  const t = s * CELL_EXTENT;
  return { a: 1 / s, b: 0, c: 0, d: 1 / s, e: -t / s, f: -t / s };
}

function cellUnitsToPxArr(): [number, number, number, number, number, number] {
  const s = CACHE_RES / CACHE_UNITS;
  const t = s * CELL_EXTENT;
  return [s, 0, 0, s, t, t];
}
